import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { apiClient } from '../utils/api'
import { colors, spacing, borderRadius, shadows, typography, transitions } from '../styles/tokens'

function LoginPage() {
  const [token, setToken] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const lastSubmitRef = useRef<number>(0)
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  
  // Configuración de debouncing para login
  const MIN_SUBMIT_INTERVAL_MS = 3000 // 3 segundos entre intentos de login

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!token.trim()) {
      setError('Please enter a valid token')
      return
    }

    // Debouncing: verificar tiempo desde último intento
    const now = Date.now()
    const timeSinceLastSubmit = now - lastSubmitRef.current
    
    if (timeSinceLastSubmit < MIN_SUBMIT_INTERVAL_MS) {
      const remainingSeconds = Math.ceil((MIN_SUBMIT_INTERVAL_MS - timeSinceLastSubmit) / 1000)
      setError(`Please wait ${remainingSeconds} seconds before trying again`)
      return
    }
    
    lastSubmitRef.current = now
    setLoading(true)
    setError('')

    try {
      const response = await apiClient.validateToken(token.trim())
      
      if (response.data?.valid && response.data['RA-subscription'] && response.data['RA-user'] && response.data['RA-API']) {
        // Token válido, guardar en store y navegar a consola
        setAuth(token.trim(), response.data['RA-subscription'], response.data['RA-user'], response.data['RA-API'])
        navigate('/consola')
      } else {
        setError(response.error || 'Invalid token')
      }
    } catch (err) {
      // Manejar errores específicos de rate limiting
      if (err instanceof Error) {
        if (err.message.includes('429') || err.message.includes('rate limit')) {
          setError('Too many attempts. Please wait before trying again.')
        } else if (err.message.includes('blocked')) {
          setError('Access temporarily blocked. Please try again later.')
        } else {
          setError('Connection error. Please try again.')
        }
      } else {
        setError('Connection error. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.background.main,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing['2xl']
    }}>
      <div style={{
        backgroundColor: colors.background.card,
        border: `1px solid ${colors.border.main}`,
        borderRadius: borderRadius.xl,
        padding: `${spacing['3xl']} ${spacing['4xl']}`,
        maxWidth: '500px',
        width: '100%',
        boxShadow: shadows.lg
      }}>
        {/* Header */}
        <div style={{ 
          textAlign: 'center',
          marginBottom: spacing['2xl']
        }}>
          <h1 style={{
            margin: `0 0 ${spacing.md} 0`,
            fontSize: typography.fontSize['3xl'],
            color: colors.text.primary,
            fontWeight: typography.fontWeight.bold,
            letterSpacing: typography.letterSpacing.normal
          }}>
            WHIN Console
          </h1>
          <p style={{
            margin: 0,
            fontSize: typography.fontSize.md,
            color: colors.text.tertiary,
            lineHeight: 1.4
          }}>
            Enter your access token to continue
          </p>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.xl,
          maxWidth: '500px',
          margin: '0 auto'
        }}>
          <div>
            <input
              id="token"
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Enter your token here"
              disabled={loading}
              autoComplete="off"
              aria-describedby={error ? "token-error" : undefined}
              autoFocus
              style={{
                width: '100%',
                padding: spacing.lg,
                fontSize: typography.fontSize.md,
                backgroundColor: colors.background.input,
                border: `1px solid ${colors.border.input}`,
                borderRadius: borderRadius.lg,
                color: colors.text.primary,
                outline: 'none',
                transition: transitions.fast,
                fontFamily: 'monospace'
              }}
              onFocus={(e) => {
                const target = e.target as HTMLInputElement
                target.style.borderColor = colors.border.focus
                target.style.boxShadow = `0 0 0 2px rgba(100, 108, 255, 0.1)`
              }}
              onBlur={(e) => {
                const target = e.target as HTMLInputElement
                target.style.borderColor = colors.border.input
                target.style.boxShadow = 'none'
              }}
            />
            {error && (
              <div id="token-error" style={{
                marginTop: spacing.md,
                padding: spacing.md,
                backgroundColor: colors.error.background,
                border: `1px solid ${colors.error.border}`,
                borderRadius: borderRadius.md,
                color: colors.error.text,
                fontSize: typography.fontSize.sm,
                textAlign: 'center'
              }} role="alert">
                ⚠️ {error}
              </div>
            )}
          </div>
          
          <button 
            type="submit" 
            disabled={loading || !token.trim()}
            style={{
              padding: `${spacing.md} ${spacing.xl}`,
              fontSize: typography.fontSize.md,
              fontWeight: typography.fontWeight.bold,
              backgroundColor: loading || !token.trim() ? colors.background.button : colors.primary,
              color: loading || !token.trim() ? colors.text.muted : colors.text.primary,
              border: 'none',
              borderRadius: borderRadius.lg,
              cursor: loading || !token.trim() ? 'not-allowed' : 'pointer',
              transition: transitions.fast,
              textTransform: 'uppercase',
              letterSpacing: typography.letterSpacing.tight
            }}
            onMouseEnter={(e) => {
              if (!loading && token.trim()) {
                const target = e.target as HTMLButtonElement
                target.style.backgroundColor = colors.primaryHover
                target.style.transform = 'translateY(-1px)'
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && token.trim()) {
                const target = e.target as HTMLButtonElement
                target.style.backgroundColor = colors.primary
                target.style.transform = 'translateY(0)'
              }
            }}
            aria-describedby="submit-help"
          >
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <span style={{ 
                  width: '16px', 
                  height: '16px', 
                  border: '2px solid transparent',
                  borderTop: '2px solid currentColor',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></span>
                Validating...
              </span>
            ) : 'Access'}
          </button>
          
          <div id="submit-help" className="sr-only">
            Press Enter or click to validate your token
          </div>
        </form>
        
        {/* Footer note */}
        <div style={{ 
          marginTop: spacing['2xl'], 
          padding: spacing.lg,
          backgroundColor: colors.background.overlay,
          borderRadius: borderRadius.lg,
          border: `1px solid ${colors.border.main}`
        }}>
          <div style={{ textAlign: 'center' }}>
              <p style={{
                margin: `0 0 ${spacing.md} 0`,
                fontSize: typography.fontSize.sm, 
                color: colors.text.muted,
                lineHeight: 1.4
              }}>
                <strong style={{ color: colors.text.tertiary }}>Need a token?</strong> Choose your API to get one:
              </p>
            
            <div style={{ 
              display: 'flex', 
              gap: spacing.lg, 
              justifyContent: 'center', 
              flexWrap: 'wrap',
              marginBottom: spacing.md
            }}>
              <a 
                href="https://rapidapi.com/inutil-inutil-default/api/mywhin" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={{ 
                  color: colors.primary, 
                  textDecoration: 'none',
                  fontSize: typography.fontSize.sm,
                  padding: `${spacing.xs} ${spacing.sm}`,
                  borderRadius: borderRadius.sm,
                  backgroundColor: colors.background.input,
                  border: `1px solid ${colors.border.input}`,
                  transition: transitions.fast
                }}
                onMouseEnter={(e) => {
                  const target = e.target as HTMLAnchorElement
                  target.style.backgroundColor = colors.primary
                  target.style.color = colors.text.primary
                }}
                onMouseLeave={(e) => {
                  const target = e.target as HTMLAnchorElement
                  target.style.backgroundColor = colors.background.input
                  target.style.color = colors.primary
                }}
              >
                myWHIN API
              </a>
              
              <a 
                href="https://rapidapi.com/inutil-inutil-default/api/mywhinlite" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={{ 
                  color: colors.primary, 
                  textDecoration: 'none',
                  fontSize: typography.fontSize.sm,
                  padding: `${spacing.xs} ${spacing.sm}`,
                  borderRadius: borderRadius.sm,
                  backgroundColor: colors.background.input,
                  border: `1px solid ${colors.border.input}`,
                  transition: transitions.fast
                }}
                onMouseEnter={(e) => {
                  const target = e.target as HTMLAnchorElement
                  target.style.backgroundColor = colors.primary
                  target.style.color = colors.text.primary
                }}
                onMouseLeave={(e) => {
                  const target = e.target as HTMLAnchorElement
                  target.style.backgroundColor = colors.background.input
                  target.style.color = colors.primary
                }}
              >
                myWHIN Lite
              </a>
            </div>
            
            <p style={{
              margin: 0,
              fontSize: typography.fontSize.xs, 
              color: colors.text.muted,
              opacity: 0.8
            }}>
              Token remains valid until you close the browser
            </p>
          </div>
        </div>
      </div>

      {/* Video Tutorial Card - Discreet Help */}
      <div style={{
        marginTop: spacing.xl,
        maxWidth: '280px',
        width: '100%',
        opacity: 0.7
      }}>
        <div style={{
          backgroundColor: colors.background.overlay,
          border: `1px solid ${colors.border.main}`,
          borderRadius: borderRadius.lg,
          padding: spacing.md,
          boxShadow: shadows.sm
        }}>
          <a 
            href="https://youtu.be/zeVEqRMn-Uc" 
            target="_blank" 
            rel="noopener noreferrer" 
            style={{ 
              textDecoration: 'none',
              display: 'block'
            }}
          >
            <div style={{
              position: 'relative',
              width: '100%',
              height: '120px', // Smaller height
              backgroundColor: '#000',
              borderRadius: borderRadius.md,
              overflow: 'hidden',
              border: `1px solid ${colors.border.input}`,
              transition: transitions.fast,
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              const target = e.target as HTMLDivElement
              target.style.transform = 'scale(1.02)'
              target.style.boxShadow = shadows.md
            }}
            onMouseLeave={(e) => {
              const target = e.target as HTMLDivElement
              target.style.transform = 'scale(1)'
              target.style.boxShadow = 'none'
            }}
            >
              <img 
                src="https://img.youtube.com/vi/zeVEqRMn-Uc/maxresdefault.jpg" 
                alt="Tutorial video thumbnail"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '50px',
                height: '50px',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                color: '#fff',
                transition: transitions.fast
              }}>
                ▶️
              </div>
            </div>
          </a>
          
          <p style={{
            margin: `${spacing.xs} 0 0 0`,
            fontSize: typography.fontSize.xs,
            color: colors.text.muted,
            textAlign: 'center',
            lineHeight: 1.3
          }}>
            Need help? Watch tutorial
          </p>
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

export default LoginPage
